import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  HairlineCard,
  Icons,
  iconSize,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useConfirm,
} from '@munifocus/ui';
import {
  BOOKING_CONFIG,
  type InspectionAvailabilityDto,
  type InspectionAvailabilityKind,
} from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { PageIntro } from '../components/PageIntro';
import { useOptionalScope } from '../providers/ScopeProvider';
import { useMutationToast } from '../lib/mutation-toast';
import {
  clockToMinutes,
  formatDay,
  formatSlot,
  formatTime,
  minutesToClock,
  useCreateAvailability,
  useDeleteAvailability,
  useInspectionBookingMe,
} from '../lib/inspection-booking-api';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0];

/** One window in words: "Every Monday, 09:00 to 12:00, 30-minute slots". */
export function useWindowSummary() {
  const { t, i18n } = useTranslation();
  return (w: InspectionAvailabilityDto) => {
    const values = {
      start: minutesToClock(w.startMinute),
      end: minutesToClock(w.endMinute),
      slot: w.slotMinutes,
    };
    return w.kind === 'weekly'
      ? t('gov.inspectionBooking.page.summaryWeekly', {
          ...values,
          day: t(`gov.inspectionBooking.page.weekdays.${w.weekday ?? 1}`),
        })
      : t('gov.inspectionBooking.page.summaryOneOff', {
          ...values,
          date: w.date ? formatDay(w.date, i18n.language) : '',
        });
  };
}

interface Draft {
  kind: InspectionAvailabilityKind;
  weekday: number;
  date: string;
  start: string;
  end: string;
  slotMinutes: number;
}

const EMPTY: Draft = {
  kind: 'weekly',
  weekday: 1,
  date: '',
  start: '09:00',
  end: '12:00',
  slotMinutes: 30,
};

/**
 * The officer's re-inspection availability: the weekly and one-off windows
 * owners book into, and the appointments already booked. Times are Moncton
 * wall-clock, entered and shown as such.
 */
export function InspectionAvailability() {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const confirm = useConfirm();
  const scope = useOptionalScope();
  const me = useInspectionBookingMe();
  const create = useCreateAvailability();
  const remove = useDeleteAvailability();
  const summary = useWindowSummary();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [lgChoice, setLgChoice] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const enabledIds = me.data?.enabledLocalGovernmentIds ?? [];
  const activeLg = scope?.active?.kind === 'local_government' ? scope.active.id : undefined;
  const lgId =
    (lgChoice && enabledIds.includes(lgChoice) && lgChoice) ||
    (activeLg && enabledIds.includes(activeLg) ? activeLg : enabledIds[0]) ||
    '';
  const lgName = (id: string) => scope?.scopes.find((s) => s.id === id)?.name ?? id;

  const add = () => {
    const startMinute = clockToMinutes(draft.start);
    const endMinute = clockToMinutes(draft.end);
    if (startMinute === null || endMinute === null || endMinute - startMinute < draft.slotMinutes) {
      setError(t('gov.inspectionBooking.page.invalidRange'));
      return;
    }
    if (draft.kind === 'one_off' && !draft.date) {
      setError(t('gov.inspectionBooking.page.dateRequired'));
      return;
    }
    setError(null);
    create.mutate(
      {
        localGovernmentId: lgId,
        kind: draft.kind,
        ...(draft.kind === 'weekly' ? { weekday: draft.weekday } : { date: draft.date }),
        startMinute,
        endMinute,
        slotMinutes: draft.slotMinutes,
      },
      {
        onSuccess: () => toast.success('gov.inspectionBooking.page.added'),
        onError: (err) => toast.error(err, 'gov.inspectionBooking.page.failed'),
      },
    );
  };

  const onRemove = async (w: InspectionAvailabilityDto) => {
    const ok = await confirm({
      title: t('gov.inspectionBooking.page.remove'),
      body: summary(w),
      tone: 'danger',
      confirmLabel: t('gov.inspectionBooking.page.remove'),
      cancelLabel: t('common.actions.cancel'),
    });
    if (!ok) return;
    remove.mutate(w.id, {
      onSuccess: () => toast.success('gov.inspectionBooking.page.removed'),
      onError: (err) => toast.error(err, 'gov.inspectionBooking.page.failed'),
    });
  };

  const windows = (me.data?.availability ?? []).filter((w) => w.localGovernmentId === lgId);

  return (
    <AppShell>
      {/* The topbar holds the title (the page's one h1); this says what the
          screen is for, and in which time zone. */}
      <PageIntro>
        {t('gov.inspectionBooking.page.intro')} {t('gov.inspectionBooking.page.timeZoneNote')}
      </PageIntro>

      {me.isLoading && <CircularProgress size={24} />}
      {me.isError && <Alert severity="error">{t('gov.inspectionBooking.page.loadError')}</Alert>}
      {me.data && enabledIds.length === 0 && (
        <Alert severity="info">{t('gov.inspectionBooking.page.off')}</Alert>
      )}

      {me.data && enabledIds.length > 0 && (
        <Stack spacing={3}>
          {enabledIds.length > 1 && (
            <TextField
              select
              label={t('gov.inspectionBooking.page.municipality')}
              value={lgId}
              onChange={(e) => setLgChoice(e.target.value)}
              sx={{ maxWidth: 360 }}
            >
              {enabledIds.map((id) => (
                <MenuItem key={id} value={id}>
                  {lgName(id)}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Box component="section" aria-labelledby="windows-title">
            <Typography id="windows-title" variant="subtitle2" component="h2" sx={{ mb: 1 }}>
              {t('gov.inspectionBooking.page.windowsTitle')}
            </Typography>
            <HairlineCard sx={{ p: 0 }}>
              {windows.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  {t('gov.inspectionBooking.page.noWindows')}
                </Typography>
              ) : (
                <Stack component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {windows.map((w) => (
                    <Stack
                      component="li"
                      key={w.id}
                      direction="row"
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1,
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2">{summary(w)}</Typography>
                      <IconButton
                        aria-label={t('gov.inspectionBooking.page.removeLabel', {
                          summary: summary(w),
                        })}
                        onClick={() => void onRemove(w)}
                        disabled={remove.isPending}
                      >
                        <Icons.Trash2 size={iconSize.sm} aria-hidden />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              )}
            </HairlineCard>
          </Box>

          <HairlineCard component="section" aria-labelledby="add-window-title" sx={{ p: 2 }}>
            <Typography id="add-window-title" variant="subtitle2" component="h2" sx={{ mb: 2 }}>
              {t('gov.inspectionBooking.page.addTitle')}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              useFlexGap
              sx={{ flexWrap: 'wrap' }}
            >
              <TextField
                select
                label={t('gov.inspectionBooking.page.kind')}
                value={draft.kind}
                onChange={(e) =>
                  setDraft({ ...draft, kind: e.target.value as InspectionAvailabilityKind })
                }
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="weekly">{t('gov.inspectionBooking.page.kinds.weekly')}</MenuItem>
                <MenuItem value="one_off">{t('gov.inspectionBooking.page.kinds.one_off')}</MenuItem>
              </TextField>
              {draft.kind === 'weekly' ? (
                <TextField
                  select
                  label={t('gov.inspectionBooking.page.weekday')}
                  value={draft.weekday}
                  onChange={(e) => setDraft({ ...draft, weekday: Number(e.target.value) })}
                  sx={{ minWidth: 160 }}
                >
                  {WEEKDAYS.map((d) => (
                    <MenuItem key={d} value={d}>
                      {t(`gov.inspectionBooking.page.weekdays.${d}`)}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  type="date"
                  label={t('gov.inspectionBooking.page.date')}
                  value={draft.date}
                  onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
              <TextField
                type="time"
                label={t('gov.inspectionBooking.page.start')}
                value={draft.start}
                onChange={(e) => setDraft({ ...draft, start: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                type="time"
                label={t('gov.inspectionBooking.page.end')}
                value={draft.end}
                onChange={(e) => setDraft({ ...draft, end: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                select
                label={t('gov.inspectionBooking.page.slotLength')}
                value={draft.slotMinutes}
                onChange={(e) => setDraft({ ...draft, slotMinutes: Number(e.target.value) })}
                sx={{ minWidth: 160 }}
              >
                {BOOKING_CONFIG.slotLengths.map((m) => (
                  <MenuItem key={m} value={m}>
                    {t('gov.inspectionBooking.page.minutes', { count: m })}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            {error && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<Icons.Plus size={iconSize.sm} aria-hidden />}
                onClick={add}
                disabled={create.isPending || !lgId}
              >
                {t('gov.inspectionBooking.page.add')}
              </Button>
            </Box>
          </HairlineCard>

          <Box component="section" aria-labelledby="upcoming-title">
            <Typography id="upcoming-title" variant="subtitle2" component="h2" sx={{ mb: 1 }}>
              {t('gov.inspectionBooking.page.upcomingTitle')}
            </Typography>
            <HairlineCard sx={{ p: 0 }}>
              {(me.data.upcoming ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  {t('gov.inspectionBooking.page.noUpcoming')}
                </Typography>
              ) : (
                <Stack component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {me.data.upcoming.map((b) => (
                    <Box
                      component="li"
                      key={b.id}
                      sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {b.startsAt ? formatSlot(b.startsAt, i18n.language) : ''}
                        {b.endsAt ? ` – ${formatTime(b.endsAt, i18n.language)}` : ''}
                      </Typography>
                      <Link
                        component={RouterLink}
                        to={`/files/${b.fileId}`}
                        aria-label={t('gov.inspectionBooking.page.openFile', {
                          reference: b.fileReference ?? '',
                        })}
                      >
                        {[b.fileReference, b.fileAddress].filter(Boolean).join(' · ')}
                      </Link>
                    </Box>
                  ))}
                </Stack>
              )}
            </HairlineCard>
          </Box>
        </Stack>
      )}
    </AppShell>
  );
}
